<script setup lang="ts" generic="T extends { id: string }">
import type { PropType, StyleValue } from 'vue';

import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
  items: {
    type: Array as PropType<Array<T>>,
    required: true,
  },
  gap: {
    type: Number,
    default: 20,
  },
  style: {
    type: [Object, String] as PropType<StyleValue>,
    required: false,
  },
});

const $root = ref<HTMLElement>();
const $container = ref<HTMLElement>();
const page = ref(1);
const pageWidth = ref(0);
const itemPerPage = ref(1);
const resizeObserver = ref<ResizeObserver>();

const touchPositionOffset = ref(0);
const movingWithTouch = ref(false);

const rightArrowPosition = ref(0);

const touchPositionUpdateDelayMs = 5;
let touchPositionXStart = 0;
let touchPositionOffsetTmp = 0;
let lastUpdate = new Date().getTime();
let touchPositionUpdateDebounce = -1;

const maxPage = computed(() => {
  return itemPerPage.value > 0 ? Math.ceil(props.items.length / itemPerPage.value) : 0;
});

const computedWidth = computed(() => pageWidth.value - Math.floor(props.gap / 2));

const canGoPreviousPage = computed(() => page.value > 1);
const canGoNextPage = computed(() => page.value < maxPage.value);

const carouselStyle = computed<StyleValue>(() => {
  const style: StyleValue = {};
  const offset = (page.value - 1) * pageWidth.value + touchPositionOffset.value;
  style.transform = `translateX(-${offset}px)`;
  if (movingWithTouch.value) {
    style.transition = 'none';
  }
  return style;
});

function updateCarouselDimensions() {
  const root = $root.value;
  if (!root)
    return;

  const computedStyle = getComputedStyle(root);
  const cardWidthValue = Number.parseFloat(computedStyle.getPropertyValue('--yb-card-width'));

  if (Number.isNaN(cardWidthValue) || cardWidthValue === 0) {
    console.error('CSS variable --yb-card-width is not defined or is zero.');
    return;
  }

  const rootWidth = root.getBoundingClientRect().width;

  itemPerPage.value = Math.max(1, Math.floor((rootWidth) / (cardWidthValue + props.gap)));

  // Corrected calculation for the slide distance
  pageWidth.value = itemPerPage.value * (cardWidthValue + props.gap);

  const containerWidth = $container.value?.getBoundingClientRect().width;
  rightArrowPosition.value = rootWidth - 8 - (containerWidth ?? 0); // Just 8px to adjust the position. Arbitrary value.
}

function onTouchStart(event: TouchEvent) {
  touchPositionXStart = event.touches[0].clientX;
  movingWithTouch.value = true;
  window.addEventListener('touchmove', onTouchMove);
  window.addEventListener('touchend', onTouchEnd);
}

function onTouchMove(event: TouchEvent) {
  touchPositionOffsetTmp = touchPositionXStart - event.touches[0].clientX;
  scheduleTouchPositionUpdate();
}

function scheduleTouchPositionUpdate() {
  const now = new Date().getTime();
  if (now - lastUpdate < touchPositionUpdateDelayMs && touchPositionUpdateDebounce !== -1) {
    return;
  }
  lastUpdate = now;
  window.clearTimeout(touchPositionUpdateDebounce);
  touchPositionUpdateDebounce = window.setTimeout(() => {
    touchPositionOffset.value = touchPositionOffsetTmp;
  }, touchPositionUpdateDelayMs);
}

function onTouchEnd() {
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend', onTouchEnd);
  window.clearTimeout(touchPositionUpdateDebounce);
  movingWithTouch.value = false;

  if (pageWidth.value === 0)
    return;

  const pageOffset = Math.round(touchPositionOffset.value / pageWidth.value);
  let newPage = page.value + pageOffset;

  if (pageOffset === 0 && Math.abs(touchPositionOffset.value) >= pageWidth.value * 0.2) {
    newPage = page.value + Math.sign(touchPositionOffset.value);
  }

  page.value = Math.max(1, Math.min(newPage, maxPage.value));
  touchPositionOffset.value = 0;
}

onMounted(() => {
  updateCarouselDimensions();
  resizeObserver.value = new ResizeObserver(updateCarouselDimensions);
  resizeObserver.value.observe($root.value!);
});

onUnmounted(() => {
  resizeObserver.value?.disconnect();
});

watch(maxPage, (newMax) => {
  if (page.value > newMax) {
    page.value = Math.max(1, newMax);
  }
});
</script>

<template>
  <div ref="$root" class="carousel-controls">
    <div ref="$container" class="carousel-container" :style="`width:${computedWidth}px`" @touchstart.passive="onTouchStart">
      <div
        class="carousel"
        :style="[carouselStyle, { gap: `${gap}px` }]"
      >
        <div v-for="item in items" :key="item.id" class="carousel-item">
          <slot name="item" :item />
        </div>
      </div>
    </div>
    <div v-if="canGoPreviousPage" role="button" tabindex="0" class="arrow" @click="page--" @keydown.enter="page--">
      <ChevronLeft />
    </div>
    <div v-if="canGoNextPage" role="button" tabindex="0" class="arrow right" :style="`right:${rightArrowPosition}px`" @click="page++" @keydown.enter="page++">
      <ChevronRight />
    </div>
  </div>
</template>

<style lang="css" scoped>
.carousel-controls {
  position: relative;
  width: 100%;
}

.carousel-container {
  overflow: hidden;
  user-select: none;
  -webkit-user-drag: none;
}

.carousel {
  display: flex;
  transition: transform ease-in-out 250ms;
}

.carousel-item {
  flex-shrink: 0;
  width: var(--yb-card-width);
  height: 100%;
}

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border-radius: 50%;
  --size: 1.3rem;
  width: var(--size);
  height: var(--size);
  padding: 0.4rem;
  cursor: pointer;
  user-select: none;
  border: 1px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.arrow:not(.right) {
  left: -16px;
}

.arrow.right {
  right: 16px;
}

.arrow > * {
  width: 100%;
  height: 100%;
}
</style>
